import { useCallback, useEffect } from 'react'
import Heading from 'LuxKit/Typography/Heading'
import { renderMarkdown } from 'lib/string/markdownUtils'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import { rem, transparentize } from 'polished'
import styled from 'styled-components'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { labelMapping } from 'marketing/pages/ContentfulWebPage/ContentfulCampaignHero'
import clsx from 'clsx'
import { useInView } from 'react-intersection-observer'
import { dynamicBannerClickEvent, dynamicBannerImpressionEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
const Wrapper = styled(Group)`
  width: ${rem(335)};
  height: 100%;

  &.size-large {
    width: ${rem(500)};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(560)};

    &.size-large {
      width: ${rem(840)};
    }
  }
`

const BannerWrapper = styled.div`
  height: ${rem(115)};
  position: relative;
  display: block;
  overflow: hidden;

  &.size-large {
    height: ${rem(170)};
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(160)};

    &.size-large {
      height: ${rem(200)};
  }
  }

  .absolute-position, .absolute-position * {
    position: absolute;
    width: 100%;
  }
`

const BannerOverlay = styled.div<{ backgroundColor: string }>`
  position: absolute;
  background: linear-gradient(
    90deg,
    ${({ backgroundColor }) => backgroundColor} 40%,
    ${({ backgroundColor }) => transparentize(0.25, backgroundColor)} 55%,
    ${({ backgroundColor }) => transparentize(0.55, backgroundColor)} 65%,
    ${({ backgroundColor }) => transparentize(1, backgroundColor)} 80%
  );
  width: 100%;
  height: 100%;
`

const BannerContentWrapper = styled.div`
  position: relative;
  padding-block: ${rem(8)} ${rem(16)};
  padding-inline: ${rem(12)} ${rem(20)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 60%;
  height: 100%;

  ${mediaQueryUp.tablet} {
    width: 100%;
    padding-block: ${rem(12)} ${rem(24)};
    padding-inline: ${rem(24)};
  }
`

const LabelWrapper = styled(Group)`
  margin-top: ${rem(12)};
`

interface Props extends App.ComponentDynamicBanner {
  size: 'medium' | 'large'
  slug: string
}

function ComponentDynamicBanner(props: Props) {
  const title = renderMarkdown(props.title)
  const footerText = props.footerText ? renderMarkdown(props.footerText) : undefined
  const footerSubText = props.footerSubText ? renderMarkdown(props.footerSubText) : undefined

  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })
  useEffect(() => {
    if (inView) {
      trackEvent(dynamicBannerImpressionEvent({
        bannerTitle: `${props.slug}_${props.title}`,
      },
      ))
    }
  }, [inView, props.title, props.slug])

  const handleBannerClick = useCallback(() => {
    trackEvent(dynamicBannerClickEvent({
      bannerTitle: `${props.slug}_${props.title}`,
    }))
  }, [props.title, props.slug])

  return (
    <ClickableLink to={props.linkTo} onClick={handleBannerClick}>
      <Wrapper direction="vertical" className={clsx(`size-${props.size}`)} ref={inViewRef}>
        <Group fullHeight fullWidth key={title} className="banner-item" direction="vertical" gap={12}>
          <BannerWrapper className={clsx(`size-${props.size}`)}>
            <ResponsiveImage className="absolute-position" id={props.image.id!} />
            <BannerOverlay backgroundColor={props.backgroundColor} />
            <BannerContentWrapper>
              {props.labels && <LabelWrapper direction="horizontal" gap={4}>
                {props.labels.map(labelMapping)}
              </LabelWrapper>}
              <Heading colour="dark-contrast" variant="heading3" dangerouslySetInnerHTML={{ __html: title }} />
            </BannerContentWrapper>
          </BannerWrapper>
          <Group direction="vertical" gap={4}>
            {footerText && <Heading variant="heading6" dangerouslySetInnerHTML={{ __html: footerText }}></Heading>}
            {footerSubText && <BodyText variant="medium" dangerouslySetInnerHTML={{ __html: footerSubText }}></BodyText>}
          </Group>
        </Group>
      </Wrapper>
    </ClickableLink>
  )
}

export default ComponentDynamicBanner
